import { useState } from 'react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SUBSCRIPTION_PLAN_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
} from '@/constants/adminLabels'
import { adminService } from '@/services/adminService'
import type {
  AdminUserListItem,
  SubscriptionPlan,
  SubscriptionStatus,
} from '@/types/admin'

interface AdminSubscriptionCardProps {
  user: AdminUserListItem
  onSaved: () => void
}

export function AdminSubscriptionCard({
  user,
  onSaved,
}: AdminSubscriptionCardProps) {
  const subscription = user.subscription
  const [plan, setPlan] = useState<SubscriptionPlan>(
    subscription?.plan ?? 'beta'
  )
  const [status, setStatus] = useState<SubscriptionStatus>(
    subscription?.status ?? 'active'
  )
  const [validUntil, setValidUntil] = useState(subscription?.validUntil ?? '')
  const [notes, setNotes] = useState(subscription?.notes ?? '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await adminService.setSubscription(user.uid, {
        plan,
        status,
        validUntil: validUntil || null,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      })
      toast.success('Prenumeration sparad')
      onSaved()
    } catch (error) {
      console.error('Failed to save subscription:', error)
      toast.error('Kunde inte spara prenumerationen')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Prenumeration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Plan</Label>
            <Select
              value={plan}
              onValueChange={(value) => setPlan(value as SubscriptionPlan)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SUBSCRIPTION_PLAN_LABELS).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as SubscriptionStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SUBSCRIPTION_STATUS_LABELS).map(
                  ([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="valid-until">Giltig till (tom = tills vidare)</Label>
          <Input
            id="valid-until"
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="subscription-notes">Anteckningar</Label>
          <Textarea
            id="subscription-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            rows={3}
          />
        </div>

        <div className="flex items-center justify-between">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Sparar...' : 'Spara prenumeration'}
          </Button>
          {subscription && (
            <p className="text-xs text-muted-foreground">
              Senast ändrad{' '}
              {format(new Date(subscription.updatedAt), 'yyyy-MM-dd HH:mm')}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
